import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import SwaggerUI from "swagger-ui-react";
import "swagger-ui-react/swagger-ui.css";
import PageTitle from "../components/common/PageTitle";
import DeveloperGuideViewer from "../components/developer-guide/DeveloperGuideViewer";
import ArchitectReviewPanel from "../components/architect/ArchitectReviewPanel";
import { apiService } from "../api/services/apiService";
import type { API } from "../types";
import { EntityStatus } from "../types";
import { formatApiDisplayName } from "../utils/apiDisplayName";
import { parseArchitectReviewJson } from "../utils/architectReview";
import {
  downloadApiSpec,
  getApiSpecDownloadFileName,
} from "../utils/apiSpecBulkUpload";
import { useCanViewStandardsReview } from "../hooks/useCanViewStandardsReview";

type ApiExplorerTab = "spec" | "guides" | "standards";

const ApiExplorerDetailPage: React.FC = () => {
  const { apiId } = useParams<{ apiId: string }>();
  const navigate = useNavigate();
  const canViewStandardsReview = useCanViewStandardsReview();
  const [api, setApi] = useState<API | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<ApiExplorerTab>("spec");

  useEffect(() => {
    if (!apiId) return;
    setLoading(true);
    apiService
      .getApi(apiId)
      .then(setApi)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load API");
      })
      .finally(() => setLoading(false));
  }, [apiId]);

  const activeGuides = useMemo(
    () =>
      (api?.developerGuides ?? []).filter(
        (guide) => guide.status === EntityStatus.ACTIVE,
      ),
    [api?.developerGuides],
  );

  const architectReview = useMemo(
    () => parseArchitectReviewJson(api?.architectReview),
    [api?.architectReview],
  );

  const parseSpecContent = (specString: string) => {
    try {
      return JSON.parse(specString);
    } catch {
      return null;
    }
  };

  const handleDownloadSpec = () => {
    if (!api?.specContent) return;
    downloadApiSpec(
      api.specContent,
      getApiSpecDownloadFileName(api.name, api.version),
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle={api ? formatApiDisplayName(api.name) : "API Details"}
        pageDescription={
          api?.description?.trim() ||
          "Read-only API specification view."
        }
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/api-explorer")}
          sx={{ mb: 3, textTransform: "none" }}
        >
          Back to API Explorer
        </Button>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {api && !loading && (
          <>
            <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={api.status}
                    color={
                      api.status === EntityStatus.ACTIVE ? "success" : "default"
                    }
                  />
                  <Chip label={`Version ${api.version}`} variant="outlined" />
                </Box>

                {api.specContent && (
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadSpec}
                    sx={{ textTransform: "none", fontWeight: 600 }}
                  >
                    Download API Spec
                  </Button>
                )}
              </Box>

              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              >
                Description
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {api.description?.trim() || "No description provided."}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <Tabs
                value={selectedTab}
                onChange={(_, value: ApiExplorerTab) => setSelectedTab(value)}
                sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
              >
                <Tab value="spec" label="OpenAPI Specification" />
                <Tab value="guides" label="Developer Guides" />
                {canViewStandardsReview && (
                  <Tab value="standards" label="Standards review" />
                )}
              </Tabs>

              <Box sx={{ p: 2 }}>
                {selectedTab === "spec" && (
                  <>
                    {api.specContent ? (
                      <SwaggerUI
                        spec={parseSpecContent(api.specContent)}
                        docExpansion="list"
                      />
                    ) : (
                      <Typography color="text.secondary">
                        No specification content available for this API.
                      </Typography>
                    )}
                  </>
                )}

                {selectedTab === "guides" && (
                  <Box>
                    {activeGuides.length > 0 ? (
                      activeGuides.map((guide) => (
                        <Box key={guide.uuid} sx={{ mb: 4 }}>
                          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            {guide.name}
                          </Typography>
                          <DeveloperGuideViewer guide={guide} />
                        </Box>
                      ))
                    ) : (
                      <Typography color="text.secondary">
                        No active developer guides linked to this API.
                      </Typography>
                    )}
                  </Box>
                )}

                {selectedTab === "standards" && canViewStandardsReview && (
                  <ArchitectReviewPanel
                    review={architectReview}
                    emptyMessage="No standards review has been recorded for this API yet."
                  />
                )}
              </Box>
            </Paper>
          </>
        )}
      </Container>
    </Box>
  );
};

export default ApiExplorerDetailPage;
